import { publisherCall, softCall, type Loaded } from './client';

const PUBLISHER_URL =
  (import.meta.env.VITE_PROMOTION_PUBLISHER_URL as string | undefined) ??
  'https://promotion.summio.org';

export type RadarPlatform = 'twitter' | 'threads' | 'instagram';
export type RadarQueue = 'opportunity' | 'conversation';
export type RadarCapability = 'direct' | 'assisted' | 'unavailable';

export interface RadarVariant {
  id: string;
  mechanic: 'dry' | 'twist' | 'useful';
  text: string;
}

export interface RadarOpportunity {
  id: string;
  queue_type: RadarQueue;
  platform: RadarPlatform;
  source_url: string;
  author_username: string | null;
  source_text: string;
  category: string;
  score: number;
  risk_score: number;
  capability: RadarCapability;
  status: string;
  variants: RadarVariant[];
  selected_text: string | null;
  published_url: string | null;
  failure_reason: string | null;
  discovered_at: string;
  expires_at: string;
}

export interface RadarSettings {
  enabled: boolean;
  read_only: boolean;
  topics: string[];
  blocked_topics: string[];
  daily_limit: number;
  min_score: number;
  brand_voice_version: string;
  automation_mode: 'auto' | 'approval';
  manual_risk_threshold: number;
  platform_accounts: Partial<Record<'twitter' | 'threads' | 'instagram', {
    method: 'official' | 'xmcp' | 'buffer' | 'assisted';
    handle: string;
    role: 'brand' | 'personal';
  } | Array<{
    method: 'official' | 'xmcp' | 'buffer' | 'assisted';
    handle: string;
    role: 'brand' | 'personal';
  }>>>;
  platform_languages: Partial<Record<'twitter' | 'threads' | 'instagram', 'en' | 'ru' | 'auto'>>;
  last_discovery_at: string | null;
}

export interface RadarPlatformStatus {
  discovery: 'active' | 'needs_connection';
  publishing: 'direct' | 'assisted' | 'unavailable';
  message: string;
}

export interface RadarQueues {
  opportunities: RadarOpportunity[];
  conversations: RadarOpportunity[];
  settings: RadarSettings | null;
  platform_status?: Partial<Record<RadarPlatform, RadarPlatformStatus>>;
}

export function loadRadar(): Promise<Loaded<RadarQueues>> {
  const fallback: RadarQueues = { opportunities: [], conversations: [], settings: null };
  return softCall(
    `${PUBLISHER_URL}/api/radar/queues?limit=100`,
    fallback,
    (json) => {
      const root = json as Partial<RadarQueues>;
      return {
        opportunities: Array.isArray(root.opportunities) ? root.opportunities : [],
        conversations: Array.isArray(root.conversations) ? root.conversations : [],
        settings: root.settings ?? null,
        platform_status: root.platform_status ?? {},
      };
    },
  );
}

export async function discoverRadar(): Promise<Record<string, unknown>> {
  return publisherCall(`${PUBLISHER_URL}/api/radar/discover`, { method: 'POST' });
}

export async function generateRadar(id: string): Promise<RadarOpportunity> {
  const json = await publisherCall<{ opportunity: RadarOpportunity }>(`${PUBLISHER_URL}/api/radar/${id}/generate`, { method: 'POST' });
  return json.opportunity;
}

export async function selectRadar(id: string, index: number): Promise<RadarOpportunity> {
  const json = await publisherCall<{ opportunity: RadarOpportunity }>(`${PUBLISHER_URL}/api/radar/${id}/select`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ index }),
  });
  return json.opportunity;
}

export async function editRadar(id: string, text: string): Promise<RadarOpportunity> {
  const json = await publisherCall<{ opportunity: RadarOpportunity }>(`${PUBLISHER_URL}/api/radar/${id}/edit`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
  });
  return json.opportunity;
}

export async function publishRadar(id: string): Promise<{ opportunity: RadarOpportunity; assisted: boolean }> {
  return publisherCall(`${PUBLISHER_URL}/api/radar/${id}/publish`, { method: 'POST' });
}

export async function skipRadar(id: string): Promise<RadarOpportunity> {
  const json = await publisherCall<{ opportunity: RadarOpportunity }>(`${PUBLISHER_URL}/api/radar/${id}/skip`, { method: 'POST' });
  return json.opportunity;
}

export async function intakeRadar(url: string, text: string): Promise<RadarOpportunity> {
  const json = await publisherCall<{ opportunity: RadarOpportunity }>(`${PUBLISHER_URL}/api/radar/intake`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url, text }),
  });
  return json.opportunity;
}
